import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';
import React from 'react';

const annotationSteps = [
  { label: '专家评语', content: '"问题意识较强"', icon: 'ri-chat-quote-line', color: 'accent' as const },
  { label: '课程编号', content: '2024-SZ-0147', icon: 'ri-barcode-line', color: 'primary' as const },
  { label: '证据片段', content: '22:10 学生回应 + 教师追问', icon: 'ri-video-line', color: 'accent' as const },
  { label: '指标标签', content: '互动设计 · 问题意识', icon: 'ri-price-tag-3-line', color: 'primary' as const },
  { label: '训练样本', content: '专家判断 ↔ 课堂证据 对应关系', icon: 'ri-database-2-line', color: 'accent' as const },
];

export default function P14ExpertToSample({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-100/14 blur-[130px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[400px] h-[400px] rounded-full bg-accent-100/12 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/3 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-100/8 blur-[160px] pointer-events-none animate-orb-float-1" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div ref={titleRef} className="text-center mb-8 md:mb-10">
          <span className={`inline-block text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            从经验到数据
          </span>
          <h2 className={`font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            一条专家意见<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">如何变成AI可学习样本</span>
          </h2>
          <p className={`text-base text-foreground-950 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            AI学习的不是"漂亮话"，而是专家判断和课堂证据之间的对应关系
          </p>
        </div>

        {/* Annotation decomposition - vertical step flow */}
        <div className={`flex flex-col items-center gap-4 md:gap-6 transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {annotationSteps.map((step, i) => (
            <React.Fragment key={step.label}>
              <div
                className={`w-full max-w-3xl group relative p-6 md:p-8 rounded-2xl transition-all duration-500 hover:scale-[1.01] flex items-center gap-5 md:gap-6 ${step.color === 'accent' ? 'glass-gold border-accent-200/30' : 'glass-liquid border-primary-200/30'}`}
                style={{ transitionDelay: `${500 + i * 120}ms` }}
              >
                <div className={`w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center shrink-0 ${step.color === 'accent' ? 'glass-gold border-accent-200/40' : 'glass-blue border-primary-200/40'}`}>
                  <i className={`${step.icon} text-2xl md:text-3xl ${step.color === 'accent' ? 'text-accent-600' : 'text-primary-600'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm md:text-base text-foreground-700 font-body mb-1">{step.label}</p>
                  <p className="text-xl md:text-2xl font-body font-semibold text-foreground-950">{step.content}</p>
                </div>
                <div className="text-2xl md:text-3xl font-heading font-bold text-foreground-200 shrink-0">{i + 1}</div>
              </div>
              {i < annotationSteps.length - 1 && (
                <div className="flex items-center justify-center">
                  <i className="ri-arrow-down-line text-2xl md:text-3xl text-primary-400 animate-bounce" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        <div className={`mt-10 md:mt-12 text-center transition-all duration-1000 delay-900 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="inline-flex items-center gap-3 px-6 py-4 rounded-xl glass border-white/50">
            <i className="ri-sparkling-line text-xl text-accent-500" />
            <span className="text-base md:text-lg font-body text-foreground-950">
              它需要被拆成：<strong className="text-primary-700">哪个问题？什么时间点？学生有没有回应？教师有没有追问？</strong>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}